import { Link } from "react-router-dom";
import HeroCarousel, { type HeroSlide } from "@/pages/home/components/HeroCarousel";
import CategoryEntries from "@/pages/home/components/CategoryEntries";
import NewsSection from "@/pages/home/components/NewsSection";
import RecommendSection from "@/pages/home/components/RecommendSection";
import CharacterSection from "@/pages/home/components/CharacterSection";
import SectionHeading from "@/components/base/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import { getNewsById, getRecommendById } from "@/lib/content";
import { newsList } from "@/mocks/news";
import { recommendList } from "@/mocks/recommend";
import { characterList } from "@/mocks/characters";

export default function Home() {
  usePageMeta({
    title: "香蕉动漫-最新动漫资讯与精彩动画推荐",
    description:
      "香蕉动漫为动漫爱好者提供丰富的动画资讯、角色介绍及动漫推荐，涵盖热门番剧和经典动画，满足您的观影需求。",
    keywords: "香蕉动漫,动漫资讯,动画推荐,热门动画,番剧介绍,动漫角色,动画更新",
  });

  const n1 = getNewsById("n1");
  const n2 = getNewsById("n2");
  const r1 = getRecommendById("r1");
  const r3 = getRecommendById("r3");

  const slides: HeroSlide[] = [
    n1 && {
      id: n1.id,
      to: `/detail/news/${n1.id}`,
      badge: "最新资讯",
      title: n1.title,
      summary: n1.summary,
      cover: n1.cover,
    },
    r1 && {
      id: r1.id,
      to: `/detail/recommend/${r1.id}`,
      badge: "编辑推荐",
      title: `${r1.title} · 星海远征的浪漫与热血`,
      summary: r1.summary,
      cover: r1.cover,
    },
    r3 && {
      id: r3.id,
      to: `/detail/recommend/${r3.id}`,
      badge: "本季新番",
      title: `${r3.title} · 当机械被赋予「心」`,
      summary: r3.summary,
      cover: r3.cover,
    },
    n2 && {
      id: n2.id,
      to: `/detail/news/${n2.id}`,
      badge: "深度报道",
      title: n2.title,
      summary: n2.summary,
      cover: n2.cover,
    },
  ].filter(Boolean) as HeroSlide[];

  const stats = [
    { icon: "ri-newspaper-line", value: `${newsList.length * 12}+`, label: "动漫资讯" },
    { icon: "ri-movie-2-line", value: `${recommendList.length * 8}+`, label: "收录番剧" },
    { icon: "ri-user-star-line", value: `${characterList.length * 15}+`, label: "角色资料" },
    { icon: "ri-heart-3-line", value: "50万+", label: "动漫爱好者" },
  ];

  return (
    <div className="w-full">
      <HeroCarousel slides={slides} />

      <div className="w-full px-4 md:px-6">
        {/* 分类快捷入口 */}
        <section className="pt-10 md:pt-14">
          <SectionHeading
            title="内容分类"
            subtitle="快速进入你感兴趣的内容专区"
          />
          <CategoryEntries />
        </section>

        {/* 最新动漫资讯 */}
        <section className="pt-12 md:pt-16">
          <SectionHeading
            title="最新动漫资讯"
            subtitle="追踪定档、开播与行业热点"
            linkTo="/category/news"
            linkLabel="全部资讯"
          />
          <NewsSection />
        </section>

        {/* 精选番剧推荐 */}
        <section className="pt-12 md:pt-16">
          <SectionHeading
            title="精选番剧推荐"
            subtitle="热门与经典，一站找齐"
            linkTo="/category/recommend"
            linkLabel="全部推荐"
          />
          <RecommendSection />
        </section>

        {/* 热门角色 */}
        <section className="pt-12 md:pt-16">
          <SectionHeading
            title="热门角色介绍"
            subtitle="认识屏幕背后有血有肉的灵魂"
            linkTo="/category/character"
            linkLabel="全部角色"
          />
          <CharacterSection />
        </section>

        {/* 站点简介 */}
        <section className="pt-12 md:pt-16">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
              <div className="lg:w-1/2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-accent-700 mb-4">
                  <i className="ri-planet-line w-4 h-4 flex items-center justify-center"></i>
                  关于香蕉动漫
                </span>
                <h2 className="font-display font-black text-xl md:text-2xl text-foreground-950">
                  最新动漫资讯与精彩动画推荐
                </h2>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                  香蕉动漫为动漫爱好者提供丰富的动画资讯、角色介绍及动漫推荐，涵盖热门番剧和经典动画。
                  无论你是追番达人还是刚刚入坑的新朋友，都能在这里发现属于自己的精彩世界。
                </p>
                <Link
                  to="/about"
                  className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-white hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解我们
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
              <div className="lg:w-1/2 grid grid-cols-2 gap-4">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-accent-700 text-xl mb-3">
                      <i className={stat.icon}></i>
                    </span>
                    <p className="font-display font-black text-xl md:text-2xl text-foreground-950">
                      {stat.value}
                    </p>
                    <p className="mt-0.5 text-xs md:text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}